import React, {useCallback, useContext, useState} from 'react';

import {Skeleton, SkeletonItem} from 'src/components/skeleton';
import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {useInvalidateUserContent} from 'src/modules/profile/user/tabs/content/hooks/useInvalidateUserContent';
import {userSavedSearchesReplaceV2} from 'src/openapi/admin/apis/UserSavedSearchesReplaceV2Api';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import {FlatButton} from '@pb/kit-component-button';
import {useShown} from '@pb/kit-hooks-use-shown';

import {InnerContentModal} from './import-content-modal.component';

import type {UserSearchesReplaceResponse} from 'src/openapi/admin/models/UserSearchesReplaceResponse';

type Props = {
    userId: number,
    accountId?: number,
};

export type ImportContentSubmitParams = {
    templateUserId: number,
    replaceSearches: boolean,
    replaceAlerts: boolean,
    replaceLayouts: boolean,
};

export const ImportContent = ({userId, accountId}: Props) => {
    const {shown, onClose, onToggle} = useShown();
    const {hasAccess} = useAdminAccessRight();
    const {userImportContentOpen, userImportContentSubmit} = useEventTracking();
    const apiDispatch = useAppDispatch();
    const {setErrorMessage} = useContext(MessageContext);
    const invalidateUserContent = useInvalidateUserContent(userId);

    const [busy, setBusy] = useState(false);
    const [migrationResult, setMigrationResult] = useState<
        UserSearchesReplaceResponse | null
    >(null);

    const onSubmitHandler = useCallback(
        async (params: ImportContentSubmitParams): Promise<string | null> => {
            setBusy(true);

            const res = await apiDispatch(
                userSavedSearchesReplaceV2({
                    userId,
                    userSearchesReplaceRequest: params,
                }),
            );

            setBusy(false);

            if (res.payload.error) {
                const error = getError({
                    error: res.payload.data,
                    separator: ' ',
                });
                setErrorMessage(error);
                return error;
            }

            setMigrationResult(res.payload.data);
            return null;
        },
        [apiDispatch, userId, setErrorMessage],
    );

    const onImportModalClose = useCallback((submit: boolean) => {
        userImportContentSubmit({submit});

        if (migrationResult) {
            invalidateUserContent();
        }

        setMigrationResult(null);
        onClose();
    }, [
        onClose,
        userImportContentSubmit,
        migrationResult,
        invalidateUserContent,
    ]);

    const disabled = !hasAccess(ACCESS_RIGHTS.USER_SCREEN_EDIT);

    const importContent = useCallback(() => {
        userImportContentOpen();
        onToggle();
    }, [userImportContentOpen, onToggle]);

    return (
        <Skeleton shown={!accountId} view={<SkeletonItem width={162} height={18}/>}>
            <FlatButton disabled={disabled} onClick={importContent}>
                Import content and layout
            </FlatButton>
            {accountId ? (
                <InnerContentModal
                    userId={userId}
                    accountId={accountId}
                    shown={shown}
                    onCloseModal={onImportModalClose}
                    onSubmitHandler={onSubmitHandler}
                    busy={busy}
                    migrationResult={migrationResult}
                />
            ) : null}
        </Skeleton>
    );
};
